import React, { useEffect } from "react";
import { Navigation } from "./navigation";
import { Footer } from "./footer";

const DetailLink = ({ slug, children, onOpenPage, className = "" }) => (
  <a
    href={`#/${slug}`}
    className={className}
    onClick={(event) => {
      if (!onOpenPage) return;
      event.preventDefault();
      onOpenPage(slug);
    }}
  >
    {children}
  </a>
);

const Hero = ({ eyebrow, title, text, visual, children }) => (
  <section className="company-hero">
    {visual}
    <div className="container company-hero-content">
      <span className="company-eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{text}</p>
      {children}
    </div>
  </section>
);

const SectionHeading = ({ eyebrow, title, text }) => (
  <div className="company-section-heading">
    {eyebrow && <span className="company-eyebrow">{eyebrow}</span>}
    <h2>{title}</h2>
    {text && <p>{text}</p>}
  </div>
);

const PageCta = ({ onHome }) => (
  <section className="company-cta">
    <div className="container company-cta-inner">
      <div>
        <span className="company-eyebrow">LET'S BUILD TOGETHER</span>
        <h2>Turn global AI capabilities into production-ready services.</h2>
      </div>
      <a
        href="#contact"
        className="company-button company-button-gold"
        onClick={(event) => {
          event.preventDefault();
          onHome("contact");
        }}
      >
        Talk to Our Team
        <i className="fa fa-arrow-right" />
      </a>
    </div>
  </section>
);

const GlobalNetworkPage = ({ data, onHome, onOpenPage }) => {
  const global = data.GlobalPresence || {};
  const hubs = global.hubs || [];
  const capabilities = global.capabilities || [];

  return (
    <>
      <Hero
        eyebrow="GLOBAL PRESENCE"
        title="Seven hubs. One connected global delivery network."
        text="Across APAC, Europe and North America, our operating network connects multi-cloud resources, model access, cross-border capabilities, regional delivery and continuous support."
        visual={
          <img
            src={global.map || "img/map/map.png"}
            alt=""
            className="company-hero-map"
          />
        }
      >
        <div className="company-hero-actions">
          <a
            href="#network-hubs"
            className="company-button company-button-gold"
          >
            Explore the Network
            <i className="fa fa-arrow-down" />
          </a>
          <DetailLink
            slug="team-expertise"
            onOpenPage={onOpenPage}
            className="company-button company-button-ghost"
          >
            Meet Our Expertise
          </DetailLink>
        </div>
      </Hero>

      <section className="company-proof-band" aria-label="Global network facts">
        <div className="container company-proof-grid">
          {(global.stats || []).map((stat, index) => (
            <div className="company-proof-item" key={index}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="network-hubs" className="company-section">
        <div className="container">
          <SectionHeading
            eyebrow="ONE OPERATING MODEL"
            title="Local roles, coordinated globally"
            text="Each hub has a distinct operating focus while sharing the same infrastructure, engineering and delivery standards."
          />
          <div className="company-hub-grid">
            {hubs.map((hub, index) => (
              <article className="company-hub-item" key={index}>
                <div className="company-hub-number">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div>
                  <i className={hub.icon || "fa fa-map-marker"} />
                  <h3>{hub.city}</h3>
                  <h4>
                    {hub.role} {hub.subrole}
                  </h4>
                  <p>{hub.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section company-section-tint">
        <div className="container">
          <SectionHeading
            eyebrow="WHAT THE NETWORK ENABLES"
            title="A global footprint designed for delivery"
            text="Our network brings resources, operations and support into one coordinated service layer."
          />
          <div className="company-capability-grid">
            {capabilities.map((capability, index) => (
              <article className="company-capability-item" key={index}>
                <i className={capability.icon} />
                <div>
                  <h3>{capability.title}</h3>
                  <p>{capability.detail || capability.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <PageCta onHome={onHome} />
    </>
  );
};

const TeamExpertisePage = ({ data, onHome, onOpenPage }) => {
  const team = data.TeamExpertise || {};
  const partners = data.GlobalPresence?.cloudPartners || [];

  return (
    <>
      <Hero
        eyebrow="TEAM & EXPERTISE"
        title="Cloud experience. AI engineering. Enterprise delivery."
        text="Our team combines experience from leading cloud and technology companies with hands-on expertise across infrastructure, model gateways, intelligent agents and production operations."
      >
        <div className="company-hero-actions">
          <a
            href="#expertise"
            className="company-button company-button-gold"
          >
            Explore Our Expertise
            <i className="fa fa-arrow-down" />
          </a>
          <DetailLink
            slug="why-goldly"
            onOpenPage={onOpenPage}
            className="company-button company-button-ghost"
          >
            Why Goldly AI
          </DetailLink>
        </div>
        <div className="company-hero-mini-stats">
          {(team.stats || []).map((stat, index) => (
            <div key={index}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </Hero>

      <section className="company-logo-band">
        <div className="container">
          <span>Experience shaped across leading cloud ecosystems</span>
          <div className="company-logo-row">
            {partners.map((partner, index) => (
              <img src={partner.logo} alt={partner.name} key={index} />
            ))}
          </div>
        </div>
      </section>

      <section id="expertise" className="company-section">
        <div className="container">
          <SectionHeading
            eyebrow="ENGINEERING DEPTH"
            title="Expertise across the complete AI delivery stack"
            text="We bring together the disciplines required to move from an AI concept to a reliable, observable and scalable production service."
          />
          <div className="company-expertise-grid">
            {(team.expertise || []).map((item, index) => (
              <article className="company-expertise-item" key={index}>
                <div className="company-expertise-index">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <i className={item.icon} />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <ul>
                  {(item.points || []).map((point, pointIndex) => (
                    <li key={pointIndex}>{point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section company-section-dark">
        <div className="container">
          <SectionHeading
            eyebrow="HOW WE DELIVER"
            title="From requirement to continuous operations"
            text="A practical delivery model keeps architecture, implementation and operations aligned from day one."
          />
          <div className="company-process">
            {(team.process || []).map((step, index) => (
              <div className="company-process-step" key={index}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCta onHome={onHome} />
    </>
  );
};

const WhyGoldlyPage = ({ data, onHome, onOpenPage }) => {
  const why = data.WhyGoldly || {};

  return (
    <>
      <Hero
        eyebrow="WHY GOLDLY AI"
        title="One operating layer from infrastructure to intelligent applications."
        text="We connect global compute, unified model access and enterprise-grade agents with the compliance, engineering and operations required for dependable production."
      >
        <div className="company-hero-actions">
          <a href="#four-pillars" className="company-button company-button-gold">
            Explore Our Advantages
            <i className="fa fa-arrow-down" />
          </a>
          <DetailLink
            slug="global-presence"
            onOpenPage={onOpenPage}
            className="company-button company-button-ghost"
          >
            View Global Network
          </DetailLink>
        </div>
        <div className="company-stack-preview">
          {(why.stack || []).map((item, index) => (
            <React.Fragment key={item.title}>
              <div>
                <span>{item.kicker}</span>
                <strong>{item.title}</strong>
              </div>
              {index < why.stack.length - 1 && (
                <i className="fa fa-arrow-right" />
              )}
            </React.Fragment>
          ))}
        </div>
      </Hero>

      <section className="company-proof-band" aria-label="Goldly AI facts">
        <div className="container company-proof-grid company-proof-grid-four">
          {(why.proof || []).map((stat, index) => (
            <div className="company-proof-item" key={index}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="four-pillars" className="company-section">
        <div className="container">
          <SectionHeading
            eyebrow="FOUR PILLARS"
            title="Capabilities that reinforce one another"
            text="Our advantage comes from combining global resources, governance, engineering and operations rather than treating them as separate services."
          />
          <div className="company-pillar-list">
            {(why.pillars || []).map((pillar, index) => (
              <article className="company-pillar-item" key={index}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <i className={pillar.icon} />
                <div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section company-section-tint">
        <div className="container">
          <SectionHeading
            eyebrow="INTEGRATED DELIVERY"
            title="A connected path to the AI-native enterprise"
            text="Each layer is valuable on its own and more powerful when delivered as one coordinated platform."
          />
          <div className="company-stack-flow">
            {(why.stack || []).map((item, index) => (
              <article key={index}>
                <span>{item.kicker}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <PageCta onHome={onHome} />
    </>
  );
};

const AgentPlatformPage = ({ data, onHome }) => {
  const agent = data.AgentPlatform || {};

  return (
    <>
      <Hero
        eyebrow="ENTERPRISE AI AGENTS"
        title="Agents that turn enterprise knowledge into coordinated action."
        text="Build secure, production-ready agents around the work your teams already do — with knowledge, tools, approvals and measurable outcomes connected through one operating layer."
        visual={
          <div className="company-agent-visual" aria-hidden="true">
            <div className="company-agent-core">
              <i className="fa fa-cogs" />
              <strong>AGENT</strong>
            </div>
            <span className="agent-node agent-node-one">KNOWLEDGE</span>
            <span className="agent-node agent-node-two">TOOLS</span>
            <span className="agent-node agent-node-three">WORKFLOWS</span>
            <span className="agent-node agent-node-four">GOVERNANCE</span>
          </div>
        }
      >
        <div className="company-hero-actions">
          <a href="#agent-capabilities" className="company-button company-button-gold">
            Explore Capabilities
            <i className="fa fa-arrow-down" />
          </a>
          <a
            href="#contact"
            className="company-button company-button-ghost"
            onClick={(event) => {
              event.preventDefault();
              onHome("contact");
            }}
          >
            Discuss a Use Case
          </a>
        </div>
      </Hero>

      <section className="company-proof-band" aria-label="Agent platform principles">
        <div className="container company-proof-grid company-proof-grid-four">
          <div className="company-proof-item"><strong>01</strong><span>Start with a measurable workflow</span></div>
          <div className="company-proof-item"><strong>02</strong><span>Connect approved knowledge and tools</span></div>
          <div className="company-proof-item"><strong>03</strong><span>Keep people and governance in control</span></div>
          <div className="company-proof-item"><strong>04</strong><span>Scale through reusable agent skills</span></div>
        </div>
      </section>

      <section id="agent-capabilities" className="company-section">
        <div className="container">
          <SectionHeading
            eyebrow="AGENT OPERATING LAYER"
            title="Everything an enterprise agent needs to do dependable work"
            text="The platform brings intelligence, execution and control into one system, so agents can do more than generate text — they can complete governed business processes."
          />
          <div className="company-expertise-grid company-agent-capability-grid">
            {(agent.capabilities || []).map((item, index) => (
              <article className="company-expertise-item" key={item.title}>
                <div className="company-expertise-index">{String(index + 1).padStart(2, "0")}</div>
                <i className={item.icon} />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section company-section-tint">
        <div className="container">
          <SectionHeading
            eyebrow="WHERE AGENTS CREATE VALUE"
            title="Designed around real operating environments"
            text="Begin with a focused use case and expand as knowledge, integrations and reusable skills mature."
          />
          <div className="company-pillar-list">
            {(agent.useCases || []).map((item, index) => (
              <article className="company-pillar-item" key={item.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <i className={item.icon} />
                <div><h3>{item.title}</h3><p>{item.text}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section">
        <div className="container">
          <SectionHeading
            eyebrow="ONE CONNECTED STACK"
            title="Agent outcomes, supported all the way down"
            text="The visible value lives in the agent experience. The gateway and infrastructure layers make that experience reliable, governable and global."
          />
          <div className="company-stack-flow company-agent-stack">
            {(agent.layers || []).map((item) => (
              <article key={item.title}>
                <span>{item.kicker}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section company-section-dark">
        <div className="container">
          <SectionHeading
            eyebrow="DELIVERY MODEL"
            title="Move from one workflow to an agent-enabled enterprise"
            text="A controlled, outcome-led path keeps adoption practical while building reusable foundations for scale."
          />
          <div className="company-process">
            {(agent.delivery || []).map((step, index) => (
              <div className="company-process-step" key={step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCta onHome={onHome} />
    </>
  );
};

/* Removed from the site at product review.
const SingaporeForumPage = ({ data, onHome }) => {
  const forum = data.IndustryForum || {};

  return (
    <>
      <Hero
        eyebrow="SINGAPORE FORUM · 21 AUGUST 2026"
        title="Where robotics, industry and agentic AI meet."
        text="GOLDLY AI joined China–Singapore government, industry, academic and technology representatives to explore practical AI collaboration and the next generation of industrial intelligence."
        visual={
          <div className="company-forum-network" aria-hidden="true">
            <div className="company-forum-network-core">
              <span>SG</span>
              <strong>AI HUB</strong>
            </div>
            <span className="company-forum-network-node node-cn">CHINA INDUSTRY</span>
            <span className="company-forum-network-node node-ai">AGENTIC AI</span>
            <span className="company-forum-network-node node-apac">APAC DELIVERY</span>
            <span className="company-forum-network-node node-global">GLOBAL MARKET</span>
          </div>
        }
      >
        <div className="company-hero-actions">
          <a href="#forum-highlights" className="company-button company-button-gold">
            View Forum Highlights
            <i className="fa fa-arrow-down" />
          </a>
          <a
            href="#contact"
            className="company-button company-button-ghost"
            onClick={(event) => {
              event.preventDefault();
              onHome("contact");
            }}
          >
            Connect in Singapore
          </a>
        </div>
      </Hero>

      <section className="company-proof-band">
        <div className="container company-proof-grid company-proof-grid-four">
          <div className="company-proof-item"><strong>SG</strong><span>Singapore industry dialogue</span></div>
          <div className="company-proof-item"><strong>AI</strong><span>Enterprise and agentic intelligence</span></div>
          <div className="company-proof-item"><strong>RBT</strong><span>Robotics and industrial ecosystem</span></div>
          <div className="company-proof-item"><strong>CN–SG</strong><span>Cross-border collaboration</span></div>
        </div>
      </section>

      <section id="forum-highlights" className="company-section forum-detail-section">
        <div className="container forum-detail-layout">
          <div className="forum-context-panel">
            <span className="company-eyebrow">FORUM CONTEXT</span>
            <h3>Singapore Robotics Industry Matchmaking Forum</h3>
            <p>
              Convened during the 2026 World Robot Conference period, the dialogue focused on China–Singapore industrial collaboration and the application of AI across robotics, enterprise and global markets.
            </p>
            <dl>
              <div><dt>Date</dt><dd>{forum.date}</dd></div>
              <div><dt>Location</dt><dd>{forum.location}</dd></div>
              <div><dt>Focus</dt><dd>Policy · Industry · Technology · Market</dd></div>
              <div><dt>GOLDLY AI</dt><dd>Agent applications · LLM Gateway · AI Infrastructure</dd></div>
            </dl>
            <div className="forum-context-path">
              <span>INDUSTRY SIGNAL</span>
              <i className="fa fa-long-arrow-down" />
              <span>AGENT OPPORTUNITY</span>
              <i className="fa fa-long-arrow-down" />
              <span>GLOBAL DELIVERY</span>
            </div>
          </div>
          <div>
            <SectionHeading
              eyebrow="WHY THIS FORUM MATTERS"
              title="A practical bridge between innovation and deployment"
              text={forum.summary}
            />
            <div className="forum-highlight-list">
              {(forum.highlights || []).map((item) => (
                <article key={item.number}>
                  <span>{item.number}</span>
                  <div><h3>{item.title}</h3><p>{item.text}</p></div>
                </article>
              ))}
            </div>
            <blockquote className="forum-detail-quote">{forum.quote}</blockquote>
          </div>
        </div>
      </section>

      <section className="company-section company-section-tint">
        <div className="container">
          <SectionHeading
            eyebrow="AGENT OPPORTUNITIES"
            title="The intelligence layer around robotics and industry"
            text="Industrial AI value is not limited to the machine itself. Agents can connect the people, knowledge, systems and cross-border processes surrounding every deployment."
          />
          <div className="company-capability-grid">
            {(forum.agentOpportunities || []).map((item) => (
              <article className="company-capability-item" key={item.title}>
                <i className={item.icon} />
                <div><h3>{item.title}</h3><p>{item.text}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="company-section company-section-dark forum-strategy-band">
        <div className="container">
          <SectionHeading
            eyebrow="GOLDLY AI · SINGAPORE"
            title="Agent applications on top. Global AI delivery underneath."
            text="Our role is to help enterprises turn this ecosystem opportunity into production systems: intelligent agents for business outcomes, a unified gateway for global and China AI models, and multi-cloud infrastructure for secure regional delivery."
          />
          <div className="forum-strategy-grid">
            <div><span>01</span><strong>Enterprise Agents</strong><p>Workflows, knowledge, tools and human oversight.</p></div>
            <div><span>02</span><strong>Global LLM Gateway</strong><p>Model access, routing, governance and observability.</p></div>
            <div><span>03</span><strong>AI Infrastructure</strong><p>Multi-cloud capacity, resilience and regional delivery.</p></div>
          </div>
        </div>
      </section>

      <PageCta onHome={onHome} />
    </>
  );
};
*/

export const CompanyDetail = ({ page, data, onHome, onOpenPage }) => {
  useEffect(() => {
    const homeTitle =
      "GOLDLY AI — Enterprise AI Agents, Global LLM Gateway & Infrastructure";
    const titles = {
      "agent-platform": "Enterprise AI Agents | GOLDLY AI",
      "global-presence": "Global Network | GOLDLY AI",
      "team-expertise": "Team & Expertise | GOLDLY AI",
      "why-goldly": "Why GOLDLY AI | GOLDLY AI",
    };
    document.title = titles[page] || homeTitle;
    window.scrollTo(0, 0);

    return () => {
      document.title = homeTitle;
    };
  }, [page]);

  return (
    <div className="company-detail-page">
      <Navigation
        detailMode
        onHome={onHome}
        onOpenPage={onOpenPage}
      />
      <main>
        {page === "agent-platform" && (
          <AgentPlatformPage data={data} onHome={onHome} />
        )}
        {page === "global-presence" && (
          <GlobalNetworkPage
            data={data}
            onHome={onHome}
            onOpenPage={onOpenPage}
          />
        )}
        {page === "team-expertise" && (
          <TeamExpertisePage
            data={data}
            onHome={onHome}
            onOpenPage={onOpenPage}
          />
        )}
        {page === "why-goldly" && (
          <WhyGoldlyPage
            data={data}
            onHome={onHome}
            onOpenPage={onOpenPage}
          />
        )}
      </main>
      <Footer contact={data.Contact} onHome={onHome} />
    </div>
  );
};
